import { z } from 'zod';
import {
  CalculatorMethodologySchema,
  CalculatorProjectCreateSchema,
  CalculatorProjectResponseSchema,
  type CalculatorMethodology,
  type CalculatorProjectCreate,
  type CalculatorProjectResponse,
} from '../contracts/calculator';
import type { CalculatorAnketaType } from '../domain/calculator-anketa-types-vocabulary';
import {
  QuoteCreateResponseSchema,
  QuoteCreateSchema,
  type QuoteCreate,
  type QuoteCreateResponse,
} from '../contracts/quote';
import type { RequestFn } from './client';

/**
 * Block 12 «Калькулятор стоимости» — apiClient namespace.
 *
 * Endpoints (ADR-0019 §Q2 methodology, §Q6 paywall, §Q7 auth, §Q11.3 idempotent UUID):
 *   - `GET /api/calculator/methodology/:type` → `CalculatorMethodology`
 *     (Service Worker CacheFirst → NetworkFirst override в Phase A4)
 *   - `GET /api/projects?kind=calculator-anketa&status=draft` → массив проектов
 *     (server-side обёртка `{ items: [...] }`, namespace разворачивает в plain array)
 *   - `GET /api/projects/:id` → `CalculatorProjectResponse`
 *   - `POST /api/projects` — idempotent upsert на client-generated UUID
 *     (ADR-0019 §Q11.3 + §Q11.8 IDB↔Server mapping) → `CalculatorProjectResponse`
 *   - `POST /api/quotes` — auto-counter naming `'{Тип} №{N}'` (ADR-0019 §Q9)
 *     → `QuoteCreateResponse`
 *
 * **Paywall (ADR-0019 §Q6 + ADR-0014):** при subscription `grace`/`expired_readonly`
 * сервер возвращает 403 `SUBSCRIPTION_REQUIRED`. `client.request` парсит ApiError
 * envelope → throws `ApiClientError(403, 'SUBSCRIPTION_REQUIRED', ...)`. UI catches
 * → render PaywallModal.
 *
 * **Auth (ADR-0019 §Q7):** `auth: true` на всех вызовах → Authorization: Bearer
 * через `client.ts` late-binding token getter. 401 → `ApiClientError('UNAUTHORIZED')`,
 * App.tsx redirect-цепочка (ADR-0014) подхватывает.
 *
 * **Client-side Zod safety net (P-012):** `saveProject` + `createQuote` валидируют
 * payload через `safeParse` ДО network call (fail-fast). Если type-checker обойдён
 * (`as any`-cast), Zod refinement отвергает request без фактического hit'а сети.
 * Симметрия с `feedback.create()`.
 *
 * **Idempotent upsert (§Q11.3):** frontend всегда вызывает `saveProject`, сервер
 * сам решает INSERT-or-UPDATE по client-generated UUID + last-write-wins по
 * `updatedAt`. POST endpoint один — нет PUT с разным URL.
 *
 * References:
 *   - ADR-0019 §Q2, §Q6, §Q7, §Q9, §Q11.3, §Q11.8
 *   - PRD `docs/prd/v4/10-block-12-calculator.md`
 *   - P-012 (typed API client), P-013 (relative URL)
 *   - Reference pattern: `feedback.ts` (Block 10), `me.ts` (Block 0 Phase 4)
 */

/** Query filter for `GET /api/projects`. */
export const CalculatorListFilterSchema = z.object({
  kind: z.literal('calculator-anketa').optional(),
  status: z.enum(['draft', 'completed']).optional(),
});
export type CalculatorListFilter = z.infer<typeof CalculatorListFilterSchema>;

const CalculatorListResponseSchema = z.object({
  items: z.array(CalculatorProjectResponseSchema),
});

export interface CalculatorApi {
  /**
   * Fetch methodology structure для anketa type (kitchen / cabinet / bath-* / tv-zone /
   * balcony / other). Service Worker CacheFirst до Phase A4 (NetworkFirst override
   * для paywall live check).
   */
  getMethodology(type: CalculatorAnketaType): Promise<CalculatorMethodology>;

  /** List user's calculator projects with optional kind/status filter. */
  listProjects(filter?: CalculatorListFilter): Promise<CalculatorProjectResponse[]>;

  /** Get single project by client-generated UUID. */
  getProject(id: string): Promise<CalculatorProjectResponse>;

  /**
   * Save project — idempotent upsert. Frontend всегда вызывает одну функцию;
   * сервер делает INSERT or UPDATE по client UUID (ADR-0019 §Q11.3). Last-write-wins
   * по `updatedAt` — older payload → 409 CONFLICT (сервер возвращает).
   */
  saveProject(data: CalculatorProjectCreate): Promise<CalculatorProjectResponse>;

  /**
   * Create quote (КП). Auto-counter naming `'{Тип} №{N}'` присваивается сервером
   * atomic transaction per (`userId`, `furnitureType`).
   */
  createQuote(data: QuoteCreate): Promise<QuoteCreateResponse>;
}

export function createCalculatorApi(request: RequestFn): CalculatorApi {
  return {
    getMethodology: (type) =>
      request({
        method: 'GET',
        path: `/api/calculator/methodology/${type}`,
        responseSchema: CalculatorMethodologySchema,
        auth: true,
      }),

    listProjects: async (filter) => {
      const parsed = filter ? CalculatorListFilterSchema.parse(filter) : {};
      const result = await request({
        method: 'GET',
        path: '/api/projects',
        query: {
          kind: parsed.kind,
          status: parsed.status,
        },
        responseSchema: CalculatorListResponseSchema,
        auth: true,
      });
      return result.items;
    },

    getProject: (id) =>
      request({
        method: 'GET',
        path: `/api/projects/${id}`,
        responseSchema: CalculatorProjectResponseSchema,
        auth: true,
      }),

    saveProject: (data) => {
      const parsed = CalculatorProjectCreateSchema.safeParse(data);
      if (!parsed.success) {
        return Promise.reject(parsed.error);
      }
      return request({
        method: 'POST',
        path: '/api/projects',
        body: parsed.data,
        responseSchema: CalculatorProjectResponseSchema,
        auth: true,
      });
    },

    createQuote: (data) => {
      const parsed = QuoteCreateSchema.safeParse(data);
      if (!parsed.success) {
        return Promise.reject(parsed.error);
      }
      return request({
        method: 'POST',
        path: '/api/quotes',
        body: parsed.data,
        responseSchema: QuoteCreateResponseSchema,
        auth: true,
      });
    },
  };
}
